import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import SegmentedTabs from '../components/ui/SegmentedTabs';
import PageHeader from '../components/ui/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Bell,
  Camera,
  Check,
  CheckCircle2,
  CircleUserRound,
  ImagePlus,
  KeyRound,
  Save,
  Shield,
  ShieldCheck,
  Trash2,
  User,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordChangeDialog from '../components/ui/PasswordChangeDialog';
import { cn } from '../lib/utils';
import { supabase } from '../lib/supabaseClient';
import { ProfileSkeleton } from '../components/ui/Skeleton';

const SETTINGS_TABS = [
  { id: 'profile', label: 'Profili yt', icon: User },
  { id: 'security', label: 'Siguria & E2E', icon: ShieldCheck },
  { id: 'notifications', label: 'Njoftimet & Audio', icon: Bell },
];

const SettingsPage = () => {
  const { user, updateProfile, updateEmail, updatePassword } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => (
    SETTINGS_TABS.some((tab) => tab.id === location.state?.activeTab)
      ? location.state.activeTab
      : 'profile'
  ));
  useEffect(() => {
    if (SETTINGS_TABS.some((tab) => tab.id === location.state?.activeTab)) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  // Profile Form state
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [username] = useState(user?.username || '');
  const [profession] = useState(user?.profession || '');
  const [email, setEmail] = useState(user?.email || '');

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setEmail(user.email || '');
    }
  }, [user]);

  // Password state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');

  // Audio & Notifications state
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('app_sound_enabled') !== 'false');
  const [chatSoundEnabled, setChatSoundEnabled] = useState(() => localStorage.getItem('app_chat_sound') !== 'false');
  const [desktopNotificationsEnabled, setDesktopNotificationsEnabled] = useState(
    () => localStorage.getItem('app_desktop_notifications') === 'true'
  );
  const [emailReminder, setEmailReminder] = useState(() => localStorage.getItem('app_email_reminder') !== 'false');
  const [reminderTime, setReminderTime] = useState(() => localStorage.getItem('app_reminder_time') || '19:00');

  // General Status
  const [loading, setLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Handle Profile Update
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setProfileMessage('');
    try {
      if (!firstName.trim() && !username.trim()) {
        setProfileSuccess(false);
        setProfileMessage('Ju lutem vendosni të paktën emrin ose emrin e përdoruesit.');
        return;
      }

      const updatedUsername = username.trim() || `${firstName.trim()} ${lastName.trim()}`.trim();
      const profileResult = await updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        profession: profession.trim() || null,
        username: updatedUsername.slice(0, 50),
      });

      if (!profileResult.success) throw new Error(profileResult.error);

      if (email !== user?.email) {
        const emailResult = await updateEmail(email);
        if (!emailResult.success) throw new Error(emailResult.error);
        setProfileSuccess(true);
        setProfileMessage('Profili u ruajt! Kontrollo email-in e ri për konfirmim.');
      } else {
        setProfileSuccess(true);
        setProfileMessage('Profili u përditësua me sukses!');
      }
    } catch (error) {
      console.error('Update failed:', error);
      setProfileSuccess(false);
      setProfileMessage('Dështoi përditësimi i profilit.');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setAvatarError('Zgjidh një fotografi JPG, PNG ose WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Fotografia duhet të jetë më e vogël se 5 MB.');
      return;
    }
    if (!user?.id) {
      setAvatarError('Duhet të jesh i kyçur për të ndryshuar fotografinë.');
      return;
    }

    setAvatarUploading(true);
    setAvatarError('');
    try {
      const extension = file.name.split('.').pop()?.replace(/[^a-zA-Z0-9]/g, '') || 'jpg';
      const path = `${user.id}/avatars/${Date.now()}.${extension}`;
      const { error: uploadError } = await supabase.storage
        .from('chat-attachments')
        .upload(path, file, { contentType: file.type, cacheControl: '3600' });
      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('chat-attachments').getPublicUrl(path);
      const result = await updateProfile({ avatar_url: data.publicUrl });
      if (!result.success) throw new Error(result.error);
      setProfileSuccess(true);
      setProfileMessage('Fotografia e profilit u përditësua.');
    } catch (error) {
      console.error('Unable to upload profile photo:', error);
      setAvatarError('Fotografia nuk u ngarkua. Provo përsëri.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarRemove = async () => {
    if (!user?.id) {
      setAvatarError('Duhet të jesh i kyçur për të hequr fotografinë.');
      return;
    }

    setAvatarUploading(true);
    setAvatarError('');
    try {
      const currentAvatarUrl = user.avatarUrl || user.avatar_url;
      const result = await updateProfile({ avatar_url: null });
      if (!result.success) throw new Error(result.error);

      let storageCleanupFailed = false;
      const storageMarker = '/storage/v1/object/public/chat-attachments/';
      const storageMarkerIndex = currentAvatarUrl?.indexOf(storageMarker) ?? -1;
      if (storageMarkerIndex !== -1) {
        const objectPath = decodeURIComponent(
          currentAvatarUrl.slice(storageMarkerIndex + storageMarker.length).split('?')[0]
        );
        const { error: removeError } = await supabase.storage
          .from('chat-attachments')
          .remove([objectPath]);
        if (removeError) {
          console.error('Profile photo was cleared but the storage object could not be removed:', removeError);
          storageCleanupFailed = true;
        }
      }

      setProfileSuccess(!storageCleanupFailed);
      setProfileMessage(storageCleanupFailed
        ? 'Fotografia u hoq nga profili, por skedari nuk u fshi plotësisht.'
        : 'Fotografia u hoq nga profili.');
    } catch (error) {
      console.error('Unable to remove profile photo:', error);
      setAvatarError('Fotografia nuk u hoq. Provo përsëri.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleDesktopNotificationsToggle = async (event) => {
    const enabled = event.target.checked;
    if (!enabled) {
      setDesktopNotificationsEnabled(false);
      return;
    }
    if (!('Notification' in window)) {
      setProfileSuccess(false);
      setProfileMessage('Ky shfletues nuk i mbështet njoftimet në desktop.');
      return;
    }
    let permission;
    try {
      permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
    } catch (error) {
      console.error('Unable to request desktop notification permission:', error);
      setProfileSuccess(false);
      setProfileMessage('Nuk u arrit të kërkohet leja për njoftimet.');
      return;
    }
    if (permission !== 'granted') {
      setDesktopNotificationsEnabled(false);
      setProfileSuccess(false);
      setProfileMessage('Lejo njoftimet në shfletues për t’i aktivizuar.');
      return;
    }
    setDesktopNotificationsEnabled(true);
    setProfileMessage('');
  };

  // Handle Password Update
  const handlePasswordReset = async (currentPassword, newPassword) => {
    setPasswordMessage('');
    setPasswordSuccess(false);
    setPasswordSubmitting(true);
    try {
      if (!user?.email) {
        setPasswordMessage('Llogaria nuk ka adresë emaili për verifikim.');
        return { success: false, error: 'Llogaria nuk ka adresë emaili për verifikim.' };
      }
      const { error: authenticationError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });
      if (authenticationError) {
        setPasswordMessage('Fjalëkalimi aktual nuk është i saktë.');
        return { success: false, error: 'Fjalëkalimi aktual nuk është i saktë.' };
      }

      const result = await updatePassword(newPassword);
      if (!result.success) {
        setPasswordMessage(result.error || 'Dështoi ndryshimi i fjalëkalimit.');
        return { success: false, error: result.error || 'Dështoi ndryshimi i fjalëkalimit.' };
      }

      setPasswordSuccess(true);
      setPasswordMessage('Fjalëkalimi u ndryshua me sukses.');
      return { success: true };
    } catch (error) {
      console.error('Unable to change account password:', error);
      setPasswordMessage('Dështoi ndryshimi i fjalëkalimit.');
      return { success: false, error: 'Dështoi ndryshimi i fjalëkalimit.' };
    } finally {
      setPasswordSubmitting(false);
    }
  };

  // Save Preferences
  const handleSavePreferences = () => {
    localStorage.setItem('app_sound_enabled', String(soundEnabled));
    localStorage.setItem('app_chat_sound', String(chatSoundEnabled));
    localStorage.setItem('app_desktop_notifications', String(desktopNotificationsEnabled));
    localStorage.setItem('app_email_reminder', String(emailReminder));
    localStorage.setItem('app_reminder_time', reminderTime);
    setProfileSuccess(true);
    setProfileMessage('Cilësimet u ruajtën me sukses!');
    setTimeout(() => setProfileMessage(''), 3000);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="page-shell-wide space-y-6 font-sans">
          <PageHeader
            title={t('Cilësimet')}
            description={t('Menaxho profilin, sigurinë dhe preferencat e aplikacionit.')}
            className="mb-0"
          />
          <ProfileSkeleton />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-shell-wide space-y-6 font-sans">
        <PageHeader
          title={t('Cilësimet')}
          description={t('Menaxho profilin, sigurinë dhe preferencat e aplikacionit.')}
          className="mb-0"
        />

        {/* Status notification toast */}
        {profileMessage && (
          <div
            className={cn(
              'flex items-center gap-2.5 rounded-2xl p-4 text-xs font-bold animate-in fade-in',
              profileSuccess
                ? 'bg-emerald-50 text-emerald-800'
                : 'bg-red-50 text-red-800'
            )}
          >
            {profileSuccess ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Shield size={18} />}
            {t(profileMessage)}
          </div>
        )}

        <div className="space-y-5">
          <div>
            <SegmentedTabs
              ariaLabel={t('Seksionet e cilësimeve')}
              tabs={SETTINGS_TABS.map((tab) => ({ ...tab, label: t(tab.label) }))}
              value={activeTab}
              onChange={(id) => {
                setActiveTab(id);
                setProfileMessage('');
              }}
            />
          </div>

          <div className="space-y-6">
            {/* ============================================================
                TAB 1: PROFILI YT
               ============================================================ */}
            {activeTab === 'profile' && (
              <Card className="overflow-hidden rounded-2xl border border-[#e3e3e3] bg-white shadow-sm">
                <CardHeader className="border-b border-zinc-100 bg-white p-5 sm:p-6 dark:bg-zinc-900">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-100 text-zinc-900">
                      <CircleUserRound size={20} />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-zinc-900">{t('Profili yt')}</CardTitle>
                      <p className="mt-0.5 text-xs text-zinc-500">
                        {t('Të dhënat që shfaqen në komunitet dhe biseda.')}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-5 sm:p-6">
                  <form onSubmit={handleUpdateProfile} className="space-y-6">
                    {/* Avatar row */}
                    <div className="flex items-center gap-4 rounded-2xl bg-zinc-50 p-4">
                      <div className={`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full text-lg font-bold ${user?.avatarUrl || user?.avatar_url ? 'bg-transparent text-zinc-900' : 'bg-zinc-700 text-white'}`}>
                        {user?.avatarUrl || user?.avatar_url ? (
                          <img src={user.avatarUrl || user.avatar_url} alt="" className="h-full w-full bg-transparent object-contain" />
                        ) : getInitials(`${firstName} ${lastName}`.trim() || 'Student')}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-zinc-900">
                          {username || `${firstName} ${lastName}` || 'Student'}
                        </h4>
                        <p className="text-xs text-zinc-500">{user?.email}</p>
                        <div className="mt-2">
                          <input
                            id="profile-avatar-file"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handleAvatarChange}
                            className="sr-only"
                            disabled={avatarUploading}
                          />
                          <label
                            htmlFor="profile-avatar-file"
                            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-zinc-950 px-3 py-2 text-[11px] font-semibold text-[#FFCE00] transition hover:bg-[#DD0000] ${avatarUploading ? 'pointer-events-none opacity-60' : ''}`}
                          >
                            {avatarUploading ? <ImagePlus size={13} className="animate-pulse" /> : <Camera size={13} />}
                            {avatarUploading ? t('Duke ngarkuar...') : t('Ndrysho fotografinë')}
                          </label>
                          {(user?.avatarUrl || user?.avatar_url) && (
                            <button
                              type="button"
                              onClick={handleAvatarRemove}
                              disabled={avatarUploading}
                              className="ml-2 inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-[11px] font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <Trash2 size={13} />
                              {t('Hiqe fotografinë')}
                            </button>
                          )}
                          <p className="mt-1 text-[10px] text-zinc-500">{t('JPG, PNG ose WebP · maksimumi 5 MB')}</p>
                          {avatarError && <p role="alert" className="mt-1 text-[11px] text-red-600">{t(avatarError)}</p>}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="firstName" className="text-xs font-bold text-zinc-700">
                          {t('Emri')}
                        </Label>
                        <Input
                          id="firstName"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder={t('Emri juaj')}
                          className="h-11 rounded-xl border-zinc-200 bg-white text-sm"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="lastName" className="text-xs font-bold text-zinc-700">
                          {t('Mbiemri')}
                        </Label>
                        <Input
                          id="lastName"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder={t('Mbiemri juaj')}
                          className="h-11 rounded-xl border-zinc-200 bg-white text-sm"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-xs font-bold text-zinc-700">
                        {t('Email Adresa')}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-11 rounded-xl border-zinc-200 bg-white text-sm"
                      />
                    </div>

                    <div className="flex justify-end pt-2">
                      <Button
                        type="submit"
                        disabled={loading}
                        className="h-10 rounded-lg px-5 text-sm font-semibold"
                      >
                        <Save size={15} />
                        {loading ? t('Duke ruajtur...') : t('Ruaj Ndryshimet')}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* ============================================================
                TAB 2: SIGURIA & ENKRIPTIMI
               ============================================================ */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                {/* Password Card */}
                <Card className="overflow-hidden rounded-2xl border border-[#e3e3e3] bg-white shadow-sm">
                    <CardHeader className="border-b border-zinc-100 bg-white p-5 sm:p-6 dark:bg-zinc-900">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-100 text-zinc-900">
                        <KeyRound size={20} />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold text-zinc-900">
                          {t('Ndrysho Fjalëkalimin')}
                        </CardTitle>
                        <p className="mt-0.5 text-xs text-zinc-500">
                          {t('Verifiko fjalëkalimin aktual dhe vendos një të ri të sigurt.')}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-5 sm:p-6">
                    {passwordMessage && (
                      <div
                        className={cn(
                          'mb-4 flex items-center gap-2 rounded-xl p-3 text-xs font-bold',
                          passwordSuccess
                            ? 'bg-emerald-50 text-emerald-800'
                            : 'bg-red-50 text-red-800'
                        )}
                      >
                        {passwordSuccess ? <Check size={16} /> : <Shield size={16} />}
                        {t(passwordMessage)}
                      </div>
                    )}

                    <div className="flex justify-end">
                      <Button
                        type="button"
                        onClick={() => {
                          setPasswordMessage('');
                          setPasswordSuccess(false);
                          setPasswordDialogOpen(true);
                        }}
                        disabled={passwordSubmitting}
                        className="h-10 rounded-lg px-4 text-sm font-semibold"
                      >
                        <KeyRound size={15} />
                        {t('Ndrysho fjalëkalimin')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

              </div>
            )}

            {/* ============================================================
                TAB 4: NJOFTIMET & AUDIO
               ============================================================ */}
            {activeTab === 'notifications' && (
              <Card className="overflow-hidden rounded-2xl border border-[#e3e3e3] bg-white shadow-sm">
              <CardHeader className="border-b border-zinc-100 bg-white p-5 sm:p-6 dark:bg-zinc-900">
                  <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-100 text-zinc-900">
                      <Bell size={20} />
                    </div>
                    <div>
                    <CardTitle className="text-base font-semibold text-zinc-900">
                        Njoftimet dhe tingujt
                      </CardTitle>
                      <p className="mt-0.5 text-xs text-zinc-500">
                        Menaxho tingujt e aplikacionit dhe njoftimet e bisedave.
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5 p-5 sm:p-6">
                  {/* Sound effects toggle */}
                  <div className="flex items-center justify-between gap-4 rounded-xl bg-zinc-50 p-3.5">
                    <div className="flex items-center gap-3">
                      <div className="brand-icon-box flex h-9 w-9 items-center justify-center rounded-xl">
                        {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-zinc-900">{t('Tingujt e ushtrimeve')}</p>
                        <p className="text-[11px] text-zinc-500">
                          {t('Luaj tingull kur përgjigjesh saktë në kuiz dhe ushtrime.')}
                        </p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={soundEnabled}
                      onChange={(e) => setSoundEnabled(e.target.checked)}
                      className="h-5 w-5 cursor-pointer rounded-md accent-[#DD0000]"
                    />
                  </div>

                  {/* Chat sound toggle */}
                  <div className="flex items-center justify-between gap-4 rounded-xl bg-zinc-50 p-3.5">
                    <div className="flex items-center gap-3">
                      <div className="brand-icon-box flex h-9 w-9 items-center justify-center rounded-xl">
                        <Bell size={18} />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-zinc-900">{t('Tingujt e bisedave')}</p>
                        <p className="text-[11px] text-zinc-500">
                          {t('Njofto me tingull kur vjen një mesazh i ri.')}
                        </p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={chatSoundEnabled}
                      onChange={(e) => setChatSoundEnabled(e.target.checked)}
                      className="h-5 w-5 cursor-pointer rounded-md accent-[#DD0000]"
                    />
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-xl bg-zinc-50 p-3.5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
                        <Bell size={18} />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-zinc-900">{t('Njoftimet në desktop')}</p>
                        <p className="text-[11px] text-zinc-500">
                          {t('Shfaq njoftim kur vjen mesazh i ri, edhe kur biseda nuk është e hapur.')}
                        </p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={desktopNotificationsEnabled}
                      onChange={handleDesktopNotificationsToggle}
                      className="h-5 w-5 cursor-pointer rounded-md accent-[#DD0000]"
                    />
                  </div>

                  {/* Daily reminder toggle & time */}
                  <div className="space-y-3 rounded-xl bg-zinc-50 p-3.5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-zinc-900">{t('Përkujtuesi ditor i mësimit')}</p>
                        <p className="text-[11px] text-zinc-500">
                          {t('Rikujtesë për të mos humbur serinë (streak).')}
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={emailReminder}
                        onChange={(e) => setEmailReminder(e.target.checked)}
                        className="h-5 w-5 cursor-pointer rounded-md accent-[#DD0000]"
                      />
                    </div>

                    {emailReminder && (
                      <div className="flex items-center justify-between rounded-xl bg-white p-2">
                        <span className="text-xs font-medium text-zinc-900">{t('Ora e rikujtesës:')}</span>
                        <input
                          type="time"
                          value={reminderTime}
                          onChange={(e) => setReminderTime(e.target.value)}
                          className="h-8 rounded-lg border-0 bg-zinc-100 px-2 text-xs font-bold text-zinc-900 focus-visible:ring-2 focus-visible:ring-[#DD0000]/30"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      type="button"
                      onClick={handleSavePreferences}
                      className="h-10 rounded-lg px-4 text-sm font-semibold"
                    >
                      {t('Ruaj Cilësimet e Audios')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

          </div>
        </div>
      </div>
      <PasswordChangeDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        onSubmit={handlePasswordReset}
      />
    </DashboardLayout>
  );
};

export default SettingsPage;
